import { useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { RefreshCcw } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import {
  OUTBOX_SERVICES,
  type OutboxService,
} from "@/features/outbox/api/endpoints";
import { useOutboxEvents } from "@/features/outbox/api/use-outbox-events";
import type { OutboxEventDto, OutboxEventStatus } from "@/features/outbox/api/dto";
import { OUTBOX_EVENT_STATUSES } from "@/features/outbox/api/dto";
import {
  OutboxReplayDialog,
  outboxEventsColumns,
  type OutboxEventRow,
} from "@/features/outbox/components";

/**
 * Supervision des `OutboxEvents` sur les 6 services (guide v2.5).
 * Sélecteur de service + liste paginée + action de rejeu sous dialog
 * (motif obligatoire, refusé 422 si pas en DEAD_LETTER).
 */
export function OutboxEventsPage() {
  const { t } = useTypedTranslation();
  const [service, setService] = useState<OutboxService>("wallet");
  const [statusFilter, setStatusFilter] = useState<OutboxEventStatus | "ALL">(
    "ALL"
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [replayEvent, setReplayEvent] = useState<OutboxEventDto | null>(null);

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useOutboxEvents(
    service,
    params
  );

  // Filtre statut client-side (l'endpoint n'accepte que page/pageSize côté
  // v2.5 — collection Postman) : applique après fetch sur la page courante.
  const rows = useMemo(
    () =>
      (data?.items ?? [])
        .filter((e): e is OutboxEventRow => Boolean(e.id))
        .filter((e) => statusFilter === "ALL" || e.status === statusFilter),
    [data, statusFilter]
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("admin.outbox.title")}
        </h1>
        <p className="text-sm text-muted-foreground max-w-3xl">
          {t("admin.outbox.subtitle")}
        </p>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex flex-col gap-1 min-w-[220px]">
          <Label htmlFor="outbox-service">{t("admin.outbox.service")}</Label>
          <Select
            value={service}
            onValueChange={(value) => {
              setService(value as OutboxService);
              setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
            }}
          >
            <SelectTrigger id="outbox-service">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTBOX_SERVICES.map((svc) => (
                <SelectItem key={svc} value={svc}>
                  {svc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 min-w-[200px]">
          <Label htmlFor="outbox-status">
            {t("admin.outbox.columns.status")}
          </Label>
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as OutboxEventStatus | "ALL")
            }
          >
            <SelectTrigger id="outbox-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t("common.all")}</SelectItem>
              {OUTBOX_EVENT_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={() => void refetch()}>
          <RefreshCcw className="mr-2 w-4 h-4" />
          {t("reload")}
        </Button>
      </div>

      <DataTableClar
        data={rows}
        columnsClar={(nav) => [
          ...outboxEventsColumns(nav),
          {
            id: "actions",
            header: t("common.actions"),
            enableHiding: false,
            cell: ({ row }) =>
              row.original.status === "DEAD_LETTER" ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReplayEvent(row.original)}
                >
                  {t("admin.outbox.rowActions.replay")}
                </Button>
              ) : null,
            meta: { type: "denied" as const },
          },
        ]}
        title={t("admin.outbox.title")}
        exportFileName={`outbox-${service}`}
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <OutboxReplayDialog
        open={replayEvent !== null}
        onOpenChange={(open) => {
          if (!open) setReplayEvent(null);
        }}
        service={service}
        event={replayEvent}
      />
    </section>
  );
}
